import { ChangeDetectionStrategy, Component, inject, Signal, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { IQuizNotification } from '../../../fi-quiz.d';
import { FiQuizHelperService } from '../../../fi-quiz-helper.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiQuizNotificationRecipientType } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz-enums';
import { FiQuizEditorStoreService } from '../../fi-quiz-editor-store.service';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';
import { Animations } from '@fi-app/shared/utils/animations';
import { AccessRightFilterCollectionModel } from '@fi-app/shared/models/access-right-filter-collection.model';

@Component({
    selector: 'fi-quiz-notifications-settings',
    templateUrl: './quiz-notifications-settings.component.html',
    styleUrl: './quiz-notifications-settings.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-quiz-notifications-settings' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiQuizNotificationsSettingsComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiQuizNotificationsSettingsComponent,
            multi: true,
        },
    ],
    animations: [Animations.fadeHeight()],
})
export class FiQuizNotificationsSettingsComponent extends ControlValueAccessorValidatorBase<IQuizNotification[]> {
    private fiQuizHelperService = inject(FiQuizHelperService);
    private fiQuizEditorStoreService = inject(FiQuizEditorStoreService);
    protected accessRightFilterCollection: Signal<AccessRightFilterCollectionModel> =
        this.fiQuizEditorStoreService.accessRightFilterCollection;

    protected openAddNotificationDialog(): void {
        this.fiQuizHelperService.openQuizNotificationEditorDialog().onYesButtonClick((result: IQuizNotification) => {
            this.ngModel.push(result);
            this.doPropagateChanges();
        });
    }

    protected openEditNotificationDialog(notificationIndex: number): void {
        this.fiQuizHelperService
            .openQuizNotificationEditorDialog(this.ngModel[notificationIndex])
            .onYesButtonClick((result: IQuizNotification) => {
                this.ngModel = this.ngModel.map((item, index) => (index === notificationIndex ? result : item));
                this.doPropagateChanges();
            });
    }

    protected deleteNotification(notificationIndex: number): void {
        this.ngModel = this.ngModel.filter((_, index) => index !== notificationIndex);
        this.doPropagateChanges();
    }

    protected getSelectedItemIds(notification: IQuizNotification): number[] {
        if (
            notification.RecipientType === FiQuizNotificationRecipientType.Roles ||
            notification.RecipientType === FiQuizNotificationRecipientType.RolesLocal
        ) {
            return notification.RoleIds;
        }

        if (notification.RecipientType === FiQuizNotificationRecipientType.Users) {
            return notification.UserIds;
        }
    }

    protected readonly QuizNotificationRecipientHighlightedTypes: INameIdObj[] = [
        {
            Id: FiQuizNotificationRecipientType.RolesLocal,
            Name: 'checklist.checkpoint_action_recipient_roles_highlighted',
        },
        {
            Id: FiQuizNotificationRecipientType.Roles,
            Name: 'checklist.checkpoint_action_recipient_roles_highlighted',
        },
        {
            Id: FiQuizNotificationRecipientType.Users,
            Name: 'checklist.checkpoint_action_recipient_staff_members_highlighted',
        },
        {
            Id: FiQuizNotificationRecipientType.EmailAddress,
            Name: 'checklist.checkpoint_action_recipient_email_address_highlighted',
        },
        {
            Id: FiQuizNotificationRecipientType.SMS,
            Name: 'checklist.checkpoint_action_recipient_sms_highlighted',
        },
    ];

    protected readonly ObjectTypes = ObjectTypes;
}
